import React, { useState, useEffect } from 'react';
import { 
  Calculator, Users, DollarSign, FileText, CheckCircle2, 
  AlertCircle, Play, Clock, Download, Eye, ChevronRight,
  RefreshCw, Send, Calendar, TrendingUp, AlertTriangle,
  Pause, X, ChevronDown, ChevronUp, Building, CreditCard,
  FileCheck, Lock, Unlock, Sparkles, Brain
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Separator } from '../../ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import { db } from '../../../lib/db';
import { 
  calculateGrossToNet, 
  PAY_FREQUENCIES, 
  EARNING_TYPES,
  formatUSDCurrency,
  calculateYTDTotals
} from '../../../lib/usPayrollCalculations';
import { formatDate } from '../../../lib/dateUtils';
import { useAuthStore } from '../../../store/authStore';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { generatePayslipPDF } from '../../../lib/export';

const RUN_TYPES = [
  { value: 'regular', label: 'Regular Payroll', description: 'Standard scheduled pay period' },
  { value: 'offcycle', label: 'Off-Cycle', description: 'Bonus, commission, or correction' },
  { value: 'adjustment', label: 'Adjustment', description: 'Retro pay or correction' }
];

const STEPS = [
  { id: 1, name: 'Setup', description: 'Configure pay period and run type' },
  { id: 2, name: 'Pre-Check', description: 'Validate employee data' },
  { id: 3, name: 'Calculate', description: 'Process gross-to-net calculations' },
  { id: 4, name: 'Review', description: 'Audit payroll register' },
  { id: 5, name: 'Submit', description: 'Finalize and create payments' }
];

// Normalize filing status from various formats to match tax calculation engine
const normalizeFilingStatus = (status) => {
  if (!status) return 'single';
  const normalized = status.toLowerCase().replace(/[_-]/g, '');
  
  const statusMap = {
    'single': 'single',
    'married': 'married',
    'marriedjointly': 'married',
    'marriedfilingjointly': 'married',
    'mfj': 'married',
    'marriedseparate': 'marriedSeparate',
    'marriedseparately': 'marriedSeparate',
    'marriedfilingseparately': 'marriedSeparate',
    'mfs': 'marriedSeparate',
    'headofhousehold': 'headOfHousehold',
    'hoh': 'headOfHousehold',
    'head': 'headOfHousehold'
  };
  
  return statusMap[normalized] || 'single';
};

export default function USPayrollRun() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1);

  // Payroll Run Configuration
  const [runConfig, setRunConfig] = useState({
    payGroupId: '',
    runType: 'regular',
    periodStart: '',
    periodEnd: '',
    checkDate: ''
  });

  // Data states
  const [payGroups, setPayGroups] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [calculatedPayslips, setCalculatedPayslips] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [stateTaxSettings, setStateTaxSettings] = useState({}); // Company state tax overrides

  // UI States
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payrollRunId, setPayrollRunId] = useState(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  // Prevent double seeding from React StrictMode
  const seedingRef = React.useRef(false);

  // Summary
  const [summary, setSummary] = useState({
    totalEmployees: 0,
    totalGross: 0,
    totalTaxes: 0,
    totalDeductions: 0,
    totalNet: 0,
    totalEmployerCost: 0
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      let [groups, users, compensation, w4Data, taxSettings] = await Promise.all([
        db.payGroups.toArray(),
        db.users.where('role').notEqual('admin').toArray(),
        db.employeeCompensation.toArray(),
        db.employeeW4.toArray(),
        db.settings.get('stateTaxSettings')
      ]);

      // Load company state tax overrides (check both 'value' and 'data' for compatibility)
      if (taxSettings) {
        setStateTaxSettings(taxSettings.value || taxSettings.data || {});
      }

      // Create default pay group if none exists and not already seeding
      if (groups.length === 0 && !seedingRef.current) {
        seedingRef.current = true;
        const defaultGroup = {
          id: uuidv4(),
          name: 'Default Bi-Weekly',
          frequency: 'biweekly',
          payDay: 'friday',
          cutoffDays: 3,
          status: 'active',
          createdAt: Date.now()
        };
        await db.payGroups.add(defaultGroup);
        groups = [defaultGroup];
      }

      // Deduplicate pay groups by name
      const seenGroups = new Set();
      groups = groups.filter(g => {
        if (seenGroups.has(g.name)) return false;
        seenGroups.add(g.name);
        return true;
      });

      setPayGroups(groups);

      // Enrich employees with compensation data
      const enrichedEmployees = users
        .filter(u => u.status === 'active')
        .map(emp => {
          const comp = compensation.find(c => c.userId === emp.id);
          const w4 = w4Data.find(w => w.userId === emp.id);
          return {
            ...emp,
            compensation: comp,
            w4: w4
          };
        });

      setEmployees(enrichedEmployees);

      // Set default dates
      const today = new Date();
      const periodEnd = new Date(today);
      periodEnd.setDate(periodEnd.getDate() + (5 - periodEnd.getDay())); // Next Friday
      const periodStart = new Date(periodEnd);
      periodStart.setDate(periodStart.getDate() - 13); // 2 weeks back
      const checkDate = new Date(periodEnd);
      checkDate.setDate(checkDate.getDate() + 7); // 1 week after period end

      setRunConfig(prev => ({
        ...prev,
        payGroupId: groups[0]?.id || '',
        periodStart: periodStart.toISOString().split('T')[0],
        periodEnd: periodEnd.toISOString().split('T')[0],
        checkDate: checkDate.toISOString().split('T')[0]
      }));

    } catch (error) {
      console.error('Error loading payroll data:', error);
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  // Get employees filtered by selected pay group
  const getFilteredEmployees = () => {
    if (!runConfig.payGroupId || runConfig.payGroupId === 'all') {
      return employees;
    }
    return employees.filter(emp => 
      emp.compensation?.payGroupId === runConfig.payGroupId
    );
  };

  // Step 1: Setup - handled by runConfig state

  // Step 2: Pre-Check Validation
  const runPreCheck = async () => {
    setProcessing(true);
    const errors = [];
    const warnings = [];

    // Use filtered employees based on selected pay group
    const filteredEmployees = getFilteredEmployees();

    if (filteredEmployees.length === 0) {
      toast.error('No employees found in the selected pay group');
      setProcessing(false);
      return;
    }

    for (const emp of filteredEmployees) {
      const employeeErrors = [];
      const employeeWarnings = [];

      // Check for compensation data
      if (!emp.compensation) {
        employeeErrors.push('Missing compensation setup');
      } else {
        if (!emp.compensation.payRate || emp.compensation.payRate <= 0) {
          employeeErrors.push('Invalid pay rate');
        }
        // Work state can come from compensation, W4, or profile - check all sources
        const workState = emp.compensation.workState || emp.w4?.workState || emp.profile?.workState;
        if (!workState) {
          employeeWarnings.push('Work state not set - no state tax will be withheld');
        }
      }

      // W-4 is optional - defaults will be applied automatically
      // Only warn if there's no W4 AND no profile data at all
      if (!emp.w4 && !emp.profile?.ssn) {
        employeeWarnings.push('No W-4 on file - using Single/0 federal defaults');
      }

      // Check for bank accounts (direct deposit)
      const bankAccounts = await db.employeeBankAccounts.where('userId').equals(emp.id).toArray();
      if (bankAccounts.length === 0) {
        employeeWarnings.push('No direct deposit setup - will generate manual check');
      }

      if (employeeErrors.length > 0) {
        errors.push({
          employeeId: emp.id,
          employeeName: `${emp.profile?.firstName} ${emp.profile?.lastName}`,
          errors: employeeErrors
        });
      }

      if (employeeWarnings.length > 0) {
        warnings.push({
          employeeId: emp.id,
          employeeName: `${emp.profile?.firstName} ${emp.profile?.lastName}`,
          warnings: employeeWarnings
        });
      }
    }

    setValidationErrors(errors);
    setValidationWarnings(warnings);

    // Auto-select employees without errors from the filtered list
    const filteredEmps = getFilteredEmployees();
    const eligibleEmployeeIds = filteredEmps
      .filter(emp => !errors.some(e => e.employeeId === emp.id) && emp.compensation)
      .map(emp => emp.id);
    setSelectedEmployees(eligibleEmployeeIds);

    setProcessing(false);

    // Always go to step 2 (Pre-Check) so users can review before calculating
    setStep(2);
    
    if (errors.length === 0) {
      toast.success('Pre-check passed! All employees validated. Review and proceed to Calculate.');
    } else {
      toast.warning(`${errors.length} employee(s) have issues that need attention`);
    }
  };

  // Step 3: Calculate Payroll
  const calculatePayroll = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    setProcessing(true);
    const results = [];

    try {
      // Create payroll run record
      const runId = uuidv4();
      setPayrollRunId(runId);

      await db.payrollRuns.add({
        id: runId,
        payGroupId: runConfig.payGroupId,
        periodStart: new Date(runConfig.periodStart).getTime(),
        periodEnd: new Date(runConfig.periodEnd).getTime(),
        checkDate: new Date(runConfig.checkDate).getTime(),
        runType: runConfig.runType,
        status: 'processing',
        createdBy: user?.id,
        createdAt: Date.now()
      });

      for (const employeeId of selectedEmployees) {
        const emp = employees.find(e => e.id === employeeId);
        if (!emp || !emp.compensation) continue;

        // Get employee data including attendance records for the pay period
        // Parse dates properly - set to start of day for periodStart and end of day for periodEnd
        const periodStartDate = new Date(runConfig.periodStart);
        periodStartDate.setHours(0, 0, 0, 0);
        const periodStart = periodStartDate.getTime();
        
        const periodEndDate = new Date(runConfig.periodEnd);
        periodEndDate.setHours(23, 59, 59, 999);
        const periodEnd = periodEndDate.getTime();
        
        const [deductions, bankAccounts, ytdPayslips, deductionCodes, attendanceRecords] = await Promise.all([
          db.employeeDeductions.where('userId').equals(employeeId).toArray(),
          db.employeeBankAccounts.where('userId').equals(employeeId).toArray(),
          db.payrollRunItems.where('userId').equals(employeeId).toArray(),
          db.deductionCodes.toArray(),
          db.attendanceRecords.where('userId').equals(employeeId).toArray()
        ]);

        // Filter attendance records for this pay period
        const periodAttendance = attendanceRecords.filter(a => {
          const recordDate = a.date;
          return recordDate >= periodStart && recordDate <= periodEnd;
        });

        // Create a map of deduction codes for quick lookup
        const deductionCodeMap = {};
        deductionCodes.forEach(dc => {
          deductionCodeMap[dc.id] = dc;
        });

        // Calculate YTD totals
        const ytd = calculateYTDTotals(ytdPayslips);

        // Determine pay frequency from selected pay group (not employee compensation)
        const selectedGroup = payGroups.find(g => g.id === runConfig.payGroupId);
        const payFrequency = selectedGroup?.frequency || emp.compensation.payFrequency || 'biweekly';
        const periodsPerYear = PAY_FREQUENCIES[payFrequency]?.periodsPerYear || 26;

        // Calculate standard hours based on pay frequency (used as fallback for hourly)
        let standardHours = 80; // Default for bi-weekly
        if (payFrequency === 'weekly') standardHours = 40;
        else if (payFrequency === 'semimonthly') standardHours = 86.67;
        else if (payFrequency === 'monthly') standardHours = 173.33;
        else if (payFrequency === 'biweekly') standardHours = 80;

        // Calculate actual hours worked from attendance records for hourly employees
        let actualHoursWorked = 0;
        let overtimeHours = 0;
        
        if (periodAttendance.length > 0) {
          // Sum up hours from attendance records
          // Fields: workHours (regular hours), overtime (OT hours)
          periodAttendance.forEach(record => {
            // Only count present days
            if (record.status === 'present' || record.workHours > 0) {
              const regularHrs = parseFloat(record.workHours) || 0;
              const otHrs = parseFloat(record.overtime) || 0;
              actualHoursWorked += regularHrs;
              overtimeHours += otHrs;
            }
          });
        }

        // Build earnings array
        const earnings = [];
        
        if (emp.compensation.payType === 'salary') {
          const perPeriodPay = parseFloat(emp.compensation.payRate) / periodsPerYear;
          earnings.push({
            type: 'regular',
            amount: perPeriodPay
          });
        } else {
          // Hourly - use actual hours worked only - do NOT default to standard hours
          // Hourly employees must have attendance records to get paid
          const hourlyRate = parseFloat(emp.compensation.payRate);
          const hoursToUse = actualHoursWorked; // Use actual hours, 0 if no attendance
          
          // Log for debugging hourly employee hours
          if (actualHoursWorked === 0) {
            console.log(`[Hourly] ${emp.profile?.firstName} ${emp.profile?.lastName}: No attendance found for period, hours = 0 (hourly employees require attendance records)`);
          } else {
            console.log(`[Hourly] ${emp.profile?.firstName} ${emp.profile?.lastName}: Using actual ${actualHoursWorked.toFixed(2)} hours from ${periodAttendance.length} attendance records`);
          }
          
          earnings.push({
            type: 'regular',
            hours: hoursToUse,
            rate: hourlyRate
          });
          
          // Add overtime if applicable - use employee's configured overtime multiplier
          if (overtimeHours > 0) {
            const overtimeMultiplier = parseFloat(emp.compensation.overtimeMultiplier) || 1.5;
            earnings.push({
              type: 'overtime',
              hours: overtimeHours,
              rate: hourlyRate * overtimeMultiplier,
              multiplier: overtimeMultiplier
            });
          }
        }

        // Build deductions arrays - map deduction codes to types recognized by calculation engine
        // The deduction type is already stored in the correct format (e.g., 'traditional401k', 'healthInsurance')
        const preTaxDeductions = deductions
          .filter(d => d.status === 'active')
          .map(d => {
            // The type field already contains the correct DEDUCTION_TYPES key
            // deductionCodeId may be the type string, or an actual code ID
            const deductionCode = deductionCodeMap[d.deductionCodeId];
            
            // First priority: use d.type if it's a valid DEDUCTION_TYPES key
            // Second priority: map from deduction code
            // Third priority: use deductionCodeId directly if it matches a type
            const codeToTypeMap = {
              '401K': 'traditional401k',
              'ROTH': 'roth401k', 
              'HSA': 'hsa',
              'FSA': 'fsa',
              'DENT': 'dentalInsurance',
              'DENTAL': 'dentalInsurance',
              'VIS': 'visionInsurance',
              'VISION': 'visionInsurance',
              'HLTH': 'healthInsurance',
              'HEALTH': 'healthInsurance',
              'LIFE': 'lifeInsurance',
              'GARN': 'creditorGarnishment',
              'GARNISH': 'creditorGarnishment'
            };
            
            // Use type directly if present, otherwise try to map from code
            let calcType = d.type;
            if (!calcType || calcType === 'other') {
              calcType = codeToTypeMap[deductionCode?.code] || codeToTypeMap[d.deductionCodeId] || d.deductionCodeId || 'other';
            }
            
            // Determine if this is a pre-tax deduction
            const preTaxTypes = [
              'traditional401k', '401k', 'traditional403b', 'traditional457', 'simpleIRA',
              'hsa', 'fsa', 'dcfsa', 'healthInsurance', 'health', 
              'dentalInsurance', 'dental', 'visionInsurance', 'vision',
              'lifeInsurance', 'life', 'commuter'
            ];
            const preTaxCodes = ['401K', 'HSA', 'FSA', 'DENTAL', 'VISION', 'HEALTH', 'LIFE', 'MEDICAL', 'HLTH', 'DENT', 'VIS'];
            
            const isPreTax = d.preTax === true || 
                            preTaxTypes.includes(calcType) || 
                            (deductionCode && preTaxCodes.includes(deductionCode.code));
            
            console.log(`[Deduction] ${d.type || d.deductionCodeId}: calcType=${calcType}, isPreTax=${isPreTax}, amount=${d.amount}`);
            
            return {
              type: calcType,
              amount: parseFloat(d.amount) || 0,
              isPercentage: d.isPercentage || false,
              preTax: isPreTax
            };
          });

        console.log(`[USPayrollRun] Employee: ${emp.profile?.firstName} ${emp.profile?.lastName}`);
        console.log(`[USPayrollRun] Raw deductions from DB:`, deductions);
        console.log(`[USPayrollRun] Mapped preTaxDeductions:`, preTaxDeductions);

        // Get work state from compensation, W4, or profile (fallback chain)
        const effectiveWorkState = emp.compensation.workState || emp.w4?.workState || emp.profile?.workState || '';
        const effectiveResidentState = emp.compensation.residentState || emp.w4?.residentState || emp.profile?.residentState || effectiveWorkState;

        // Run gross-to-net calculation
        const calculation = calculateGrossToNet({
          employeeId: emp.id,
          employeeName: `${emp.profile?.firstName} ${emp.profile?.lastName}`,
          earnings,
          payFrequency,
          filingStatus: normalizeFilingStatus(emp.w4?.filingStatus),
          additionalFederalWithholding: emp.w4?.additionalWithholding || 0,
          workState: effectiveWorkState,
          residentState: effectiveResidentState,
          preTaxDeductions,
          postTaxDeductions: [],
          ytdGrossWages: ytd.grossWages,
          ytdFederalTax: ytd.federalTax,
          ytdSocialSecurity: ytd.socialSecurity,
          ytdMedicare: ytd.medicare
        });

        // Apply company state tax overrides if any exist for this employee's work state
        const stateOverrides = stateTaxSettings[effectiveWorkState];
        if (stateOverrides && calculation.taxes?.state) {
          // Check if state withholding is disabled
          if (stateOverrides.disabled) {
            calculation.taxes.state.amount = 0;
            calculation.taxes.state.overrideApplied = 'disabled';
          } else {
            // Apply rate override for flat tax states
            if (stateOverrides.rateOverride !== undefined && stateOverrides.rateOverride !== null) {
              const taxableGross = calculation.preTaxDeductions?.taxableGross || calculation.earnings?.grossPay || 0;
              const annualizedTaxable = taxableGross * (PAY_FREQUENCIES[payFrequency]?.periodsPerYear || 26);
              const newAnnualTax = annualizedTaxable * stateOverrides.rateOverride;
              calculation.taxes.state.amount = Math.round((newAnnualTax / (PAY_FREQUENCIES[payFrequency]?.periodsPerYear || 26)) * 100) / 100;
              calculation.taxes.state.overrideApplied = 'rateOverride';
              calculation.taxes.state.overrideRate = stateOverrides.rateOverride;
            }
            
            // Apply additional withholding
            if (stateOverrides.additionalWithholding && stateOverrides.additionalWithholding > 0) {
              calculation.taxes.state.amount += stateOverrides.additionalWithholding;
              calculation.taxes.state.additionalWithholding = stateOverrides.additionalWithholding;
            }
          }
          
          // Recalculate total taxes and net pay after applying overrides
          calculation.taxes.totalTaxes = 
            (calculation.taxes.federal?.amount || 0) +
            (calculation.taxes.state?.amount || 0) +
            (calculation.taxes.local?.amount || 0) +
            (calculation.taxes.socialSecurity?.amount || 0) +
            (calculation.taxes.medicare?.amount || 0);
          
          calculation.netPay = 
            (calculation.earnings?.grossPay || 0) -
            (calculation.preTaxDeductions?.total || 0) -
            calculation.taxes.totalTaxes -
            (calculation.postTaxDeductions?.total || 0);
        }

        console.log(`[USPayrollRun] Calculation Result for ${emp.profile?.firstName}:`, {
          grossPay: calculation.earnings.grossPay,
          preTaxTotal: calculation.preTaxDeductions.total,
          taxableGross: calculation.preTaxDeductions.taxableGross,
          federalTax: calculation.taxes.federal.amount,
          socialSecurity: calculation.taxes.socialSecurity.amount,
          medicare: calculation.taxes.medicare.amount,
          stateTax: calculation.taxes.state.amount,
          stateOverrideApplied: calculation.taxes.state.overrideApplied || 'none',
          netPay: calculation.netPay
        });

        // Add bank account info
        calculation.bankAccounts = bankAccounts;
        calculation.employee = emp;

        results.push(calculation);

        // Save run item to database
        await db.payrollRunItems.add({
          id: uuidv4(),
          payrollRunId: runId,
          userId: emp.id,
          grossPay: calculation.earnings.grossPay,
          preTaxDeductions: calculation.preTaxDeductions.total,
          taxableWages: calculation.preTaxDeductions.taxableGross,
          federalTax: calculation.taxes.federal.amount,
          stateTax: calculation.taxes.state.amount,
          localTax: calculation.taxes.local.amount,
          socialSecurity: calculation.taxes.socialSecurity.amount,
          medicare: calculation.taxes.medicare.amount,
          postTaxDeductions: calculation.postTaxDeductions.total,
          netPay: calculation.netPay,
          calculationDetails: JSON.stringify(calculation),
          status: 'calculated',
          createdAt: Date.now()
        });
      }

      setCalculatedPayslips(results);

      // Calculate summary
      const summaryData = results.reduce((acc, calc) => ({
        totalEmployees: acc.totalEmployees + 1,
        totalGross: acc.totalGross + calc.earnings.grossPay,
        totalTaxes: acc.totalTaxes + calc.taxes.totalTaxes,
        totalDeductions: acc.totalDeductions + calc.preTaxDeductions.total + calc.postTaxDeductions.total,
        totalNet: acc.totalNet + calc.netPay,
        totalEmployerCost: acc.totalEmployerCost + calc.totalEmployerCost
      }), {
        totalEmployees: 0,
        totalGross: 0,
        totalTaxes: 0,
        totalDeductions: 0,
        totalNet: 0,
        totalEmployerCost: 0
      });

      setSummary(summaryData);

      // Update run status
      await db.payrollRuns.update(runId, { 
        status: 'calculated',
        processedAt: Date.now()
      });

      toast.success(`Calculated payroll for ${results.length} employees`);
      setStep(4);

    } catch (error) {
      console.error('Error calculating payroll:', error);
      toast.error('Failed to calculate payroll');
    } finally {
      setProcessing(false);
    }
  };

  // Step 5: Submit Payroll
  const handleSubmitClick = () => {
    setShowSubmitConfirm(true);
  };

  const submitPayroll = async () => {
    setShowSubmitConfirm(false);
    setProcessing(true);

    try {
      // Calculate totals from calculatedPayslips for storage
      const totalGross = calculatedPayslips.reduce((sum, c) => sum + (c.grossPay || 0), 0);
      const totalTaxes = calculatedPayslips.reduce((sum, c) => sum + (c.taxes?.totalTaxes || 0), 0);
      const totalDeductions = calculatedPayslips.reduce((sum, c) => 
        sum + (c.preTaxDeductions?.total || 0) + (c.postTaxDeductions?.total || 0), 0);
      const totalNet = calculatedPayslips.reduce((sum, c) => sum + (c.netPay || 0), 0);
      const totalEmployerTaxes = calculatedPayslips.reduce((sum, c) => 
        sum + (c.employerTaxes?.total || 0), 0);

      // Update payroll run status with summary totals
      await db.payrollRuns.update(payrollRunId, {
        status: 'submitted',
        submittedAt: Date.now(),
        submittedBy: user?.id,
        employeeCount: calculatedPayslips.length,
        totalGross: Math.round(totalGross * 100) / 100,
        totalTaxes: Math.round(totalTaxes * 100) / 100,
        totalDeductions: Math.round(totalDeductions * 100) / 100,
        totalNet: Math.round(totalNet * 100) / 100,
        totalEmployerTaxes: Math.round(totalEmployerTaxes * 100) / 100,
        // Also store in summary object for backward compatibility
        summary: {
          totalGross: Math.round(totalGross * 100) / 100,
          totalTaxes: Math.round(totalTaxes * 100) / 100,
          totalDeductions: Math.round(totalDeductions * 100) / 100,
          totalNet: Math.round(totalNet * 100) / 100,
          totalEmployerTaxes: Math.round(totalEmployerTaxes * 100) / 100,
          employeeCount: calculatedPayslips.length
        }
      });

      // Get period info for payslips
      const periodStart = new Date(runConfig.periodStart);
      const periodEnd = new Date(runConfig.periodEnd);
      const checkDate = new Date(runConfig.checkDate);
      const periodName = periodStart.toLocaleString('default', { month: 'long', year: 'numeric' });
      const selectedPayGroup = payGroups.find(g => g.id === runConfig.payGroupId);

      // Create payslip records for each employee
      const payslipRecords = calculatedPayslips.map(calc => {
        // Extract values from calculation result with proper paths
        const grossPay = calc.earnings?.grossPay || calc.grossPay || 0;
        const totalPreTax = calc.preTaxDeductions?.total || calc.totalPreTax || 0;
        const totalTaxes = calc.taxes?.totalTaxes || calc.totalTaxes || 0;
        const totalPostTax = calc.postTaxDeductions?.total || calc.totalPostTax || 0;
        const netPay = calc.netPay || 0;
        
        // Extract individual pre-tax deduction amounts
        const preTaxDeds = calc.preTaxDeductions?.deductions || [];
        const preTaxByType = {};
        preTaxDeds.forEach(d => {
          preTaxByType[d.type] = d.amount;
        });
        
        // Extract individual post-tax deduction amounts
        const postTaxDeds = calc.postTaxDeductions?.deductions || [];
        const postTaxByType = {};
        postTaxDeds.forEach(d => {
          postTaxByType[d.type] = d.amount;
        });
        
        return {
          id: uuidv4(),
          userId: calc.employeeId,
          payrollRunId: payrollRunId,
          periodName: periodName,
          month: periodStart.getMonth() + 1,
          year: periodStart.getFullYear(),
          payFrequency: selectedPayGroup?.frequency || 'biweekly',
          
          // US Earnings breakdown
          earnings: {
            regular: grossPay,
            overtime: calc.earnings?.breakdown?.find(e => e.type === 'overtime')?.amount || 0,
            bonus: calc.earnings?.breakdown?.find(e => e.type === 'bonus')?.amount || 0,
            commission: calc.earnings?.breakdown?.find(e => e.type === 'commission')?.amount || 0,
            holiday: calc.earnings?.breakdown?.find(e => e.type === 'holiday')?.amount || 0,
            pto: calc.earnings?.breakdown?.find(e => e.type === 'pto')?.amount || 0
          },
          grossPay: grossPay,
          
          // Pre-tax deductions
          preTaxDeductions: {
            retirement401k: preTaxByType.traditional401k || preTaxByType.retirement401k || 0,
            healthInsurance: preTaxByType.healthInsurance || 0,
            hsa: preTaxByType.hsa || 0,
            fsa: preTaxByType.fsa || 0,
            dental: preTaxByType.dental || 0,
            vision: preTaxByType.vision || 0
          },
          totalPreTax: totalPreTax,
          taxableGross: calc.preTaxDeductions?.taxableGross || grossPay - totalPreTax,
          
          // Taxes (US style)
          taxes: {
            federalIncome: calc.taxes?.federal?.amount || 0,
            socialSecurity: calc.taxes?.socialSecurity?.amount || 0,
            medicare: calc.taxes?.medicare?.amount || 0,
            stateIncome: calc.taxes?.state?.amount || 0,
            stateName: calc.taxes?.state?.stateName || calc.workState || '',
            sdi: calc.taxes?.sdi?.amount || 0,
            localTax: calc.taxes?.local?.amount || 0
          },
          totalTaxes: totalTaxes,
          
          // Post-tax deductions
          postTaxDeductions: {
            roth401k: postTaxByType.roth401k || 0,
            lifeInsurance: postTaxByType.lifeInsurance || 0,
            garnishment: postTaxByType.garnishment || postTaxByType.childSupport || 0,
            other: postTaxByType.other || 0
          },
          totalPostTax: totalPostTax,
          
          // Net pay
          netPay: netPay,
          
          // W-4 info
          w4Info: {
            filingStatus: calc.employee?.w4?.filingStatus || 'single',
            dependentCredit: calc.employee?.w4?.dependentCredit || 0,
            additionalWithholding: calc.employee?.w4?.additionalWithholding || 0
          },
          
          // Tax jurisdiction
          workState: calc.workState || calc.employee?.compensation?.workState || '',
          residentState: calc.residentState || calc.employee?.compensation?.residentState || '',
          
          // YTD totals
          ytd: calc.ytd || {
            grossPay: grossPay,
            federalTax: calc.taxes?.federal?.amount || 0,
            socialSecurity: calc.taxes?.socialSecurity?.amount || 0,
            medicare: calc.taxes?.medicare?.amount || 0,
            stateTax: calc.taxes?.state?.amount || 0,
            retirement401k: preTaxByType.traditional401k || preTaxByType.retirement401k || 0,
            netPay: netPay
          },
          
          // Legacy fields for backward compatibility
          grossEarnings: grossPay,
          grossSalary: grossPay,
          totalDeductions: totalPreTax + totalTaxes + totalPostTax,
          netSalary: netPay,
          workingDays: 22,
          daysWorked: 22,
          lop: 0,
          
          status: 'paid',
          generatedAt: Date.now(),
          paidAt: checkDate.getTime(),
          paymentMethod: calc.bankAccounts?.length > 0 ? 'direct_deposit' : 'check',
          createdAt: Date.now()
        };
      });

      await db.payslips.bulkAdd(payslipRecords);

      // Create aggregated tax liabilities (one entry per tax type per payroll run)
      // Aggregate all employee taxes into single liability records
      const federalWithholding = calculatedPayslips.reduce((sum, calc) => sum + (calc.taxes.federal.amount || 0), 0);
      const ficaTotal = calculatedPayslips.reduce((sum, calc) => 
        sum + (calc.taxes.socialSecurity.amount || 0) + (calc.taxes.medicare.amount || 0) + 
              (calc.employerTaxes.socialSecurity || 0) + (calc.employerTaxes.medicare || 0), 0);
      
      // Group state taxes by state
      const stateWithholdings = {};
      calculatedPayslips.forEach(calc => {
        const state = calc.employee.compensation?.workState;
        if (state && calc.taxes.state.amount > 0) {
          if (!stateWithholdings[state]) {
            stateWithholdings[state] = 0;
          }
          stateWithholdings[state] += calc.taxes.state.amount;
        }
      });

      const taxLiabilities = [];
      
      // Federal income tax withholding (aggregated)
      if (federalWithholding > 0) {
        taxLiabilities.push({
          id: uuidv4(),
          payrollRunId,
          jurisdiction: 'federal',
          taxType: 'withholding',
          amount: federalWithholding,
          dueDate: Date.now() + 15 * 24 * 60 * 60 * 1000, // 15 days
          status: 'pending',
          createdAt: Date.now()
        });
      }

      // FICA (SS + Medicare) - employee + employer portions (aggregated)
      if (ficaTotal > 0) {
        taxLiabilities.push({
          id: uuidv4(),
          payrollRunId,
          jurisdiction: 'federal',
          taxType: 'fica',
          amount: ficaTotal,
          dueDate: Date.now() + 15 * 24 * 60 * 60 * 1000,
          status: 'pending',
          createdAt: Date.now()
        });
      }

      // State taxes (aggregated by state)
      Object.entries(stateWithholdings).forEach(([state, amount]) => {
        taxLiabilities.push({
          id: uuidv4(),
          payrollRunId,
          jurisdiction: 'state',
          state: state,
          taxType: 'withholding',
          amount: amount,
          dueDate: Date.now() + 30 * 24 * 60 * 60 * 1000,
          status: 'pending',
          createdAt: Date.now()
        });
      });

      await db.taxLiabilities.bulkAdd(taxLiabilities);

      // Create ACH batch for direct deposits
      const achBatchId = uuidv4();
      const directDepositEmployees = calculatedPayslips.filter(c => c.bankAccounts?.length > 0);

      if (directDepositEmployees.length > 0) {
        await db.achBatches.add({
          id: achBatchId,
          payrollRunId,
          batchType: 'payroll',
          totalAmount: directDepositEmployees.reduce((sum, c) => sum + c.netPay, 0),
          effectiveDate: new Date(runConfig.checkDate).getTime(),
          status: 'created',
          createdAt: Date.now()
        });

        // Create individual ACH transactions
        const achTransactions = [];
        for (const calc of directDepositEmployees) {
          for (const bank of calc.bankAccounts) {
            let amount = calc.netPay;
            if (bank.depositType === 'fixed') {
              amount = Math.min(parseFloat(bank.amount), calc.netPay);
            } else if (bank.depositType === 'percentage') {
              amount = calc.netPay * (bank.percentage / 100);
            }

            achTransactions.push({
              id: uuidv4(),
              achBatchId,
              userId: calc.employeeId,
              amount,
              accountNumber: bank.accountNumber,
              routingNumber: bank.routingNumber,
              transactionType: bank.accountType === 'savings' ? 'savings_credit' : 'checking_credit',
              status: 'pending',
              createdAt: Date.now()
            });
          }
        }
        await db.achTransactions.bulkAdd(achTransactions);
      }

      // Create manual checks for employees without direct deposit
      const manualCheckEmployees = calculatedPayslips.filter(c => !c.bankAccounts?.length);
      if (manualCheckEmployees.length > 0) {
        const manualChecks = manualCheckEmployees.map((calc, index) => ({
          id: uuidv4(),
          payrollRunId,
          userId: calc.employeeId,
          checkNumber: `${new Date().getFullYear()}${String(index + 1).padStart(6, '0')}`,
          amount: calc.netPay,
          issueDate: new Date(runConfig.checkDate).getTime(),
          status: 'pending',
          createdAt: Date.now()
        }));
        await db.manualChecks.bulkAdd(manualChecks);
      }

      toast.success('Payroll submitted successfully!');
      setStep(5);

    } catch (error) {
      console.error('Error submitting payroll:', error);
      toast.error('Failed to submit payroll');
    } finally {
      setProcessing(false);
    }
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const toggleAllEmployees = () => {
    const filteredEmps = getFilteredEmployees();
    const eligibleIds = filteredEmps
      .filter(emp => !validationErrors.some(e => e.employeeId === emp.id) && emp.compensation)
      .map(emp => emp.id);

    if (selectedEmployees.length === eligibleIds.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(eligibleIds);
    }
  };

  // Download individual payslip
  const downloadIndividualPayslip = (payslip) => {
    if (!payslip) return;

    try {
      // Map calculation result to payslip format for PDF generation
      const payslipData = {
        periodStart: runConfig.periodStart,
        periodEnd: runConfig.periodEnd,
        payDate: runConfig.checkDate,
        grossPay: payslip.grossPay || payslip.earnings?.grossPay || 0,
        netPay: payslip.netPay || 0,
        totalTaxes: payslip.taxes?.totalTaxes || payslip.totalTaxes || 0,
        totalPreTax: payslip.totalPreTax || payslip.preTaxDeductions?.total || 0,
        totalPostTax: payslip.totalPostTax || payslip.postTaxDeductions?.total || 0,
        earnings: {
          regular: payslip.earnings?.regular || payslip.grossPay || 0,
          overtime: payslip.earnings?.overtime || 0,
          bonus: payslip.earnings?.bonus || 0,
          commission: payslip.earnings?.commission || 0,
          total: payslip.earnings?.grossPay || payslip.grossPay || 0
        },
        taxes: {
          federalIncome: payslip.taxes?.federal?.amount || payslip.federalTax || 0,
          socialSecurity: payslip.taxes?.socialSecurity?.amount || payslip.socialSecurity || 0,
          medicare: payslip.taxes?.medicare?.amount || payslip.medicare || 0,
          stateIncome: payslip.taxes?.state?.amount || payslip.stateTax || 0,
          stateName: payslip.workState
        },
        preTaxDeductions: [
          { type: '401(k)', amount: payslip.preTaxDeductions?.retirement401k || 0 },
          { type: 'Health Insurance', amount: payslip.preTaxDeductions?.healthInsurance || 0 },
          { type: 'HSA', amount: payslip.preTaxDeductions?.hsa || 0 },
          { type: 'FSA', amount: payslip.preTaxDeductions?.fsa || 0 },
          { type: 'Dental', amount: payslip.preTaxDeductions?.dental || 0 },
          { type: 'Vision', amount: payslip.preTaxDeductions?.vision || 0 }
        ].filter(d => d.amount > 0),
        postTaxDeductions: [
          { type: 'Roth 401(k)', amount: payslip.postTaxDeductions?.roth401k || 0 },
          { type: 'Garnishment', amount: payslip.postTaxDeductions?.garnishment || payslip.garnishment || 0 }
        ].filter(d => d.amount > 0),
        ytd: payslip.ytd || {}
      };

      const employeeData = {
        profile: {
          firstName: payslip.employeeName?.split(' ')[0] || 'Employee',
          lastName: payslip.employeeName?.split(' ').slice(1).join(' ') || '',
          department: payslip.department || 'N/A',
          position: payslip.position || 'N/A'
        }
      };

      generatePayslipPDF(payslipData, employeeData);
      toast.success('Payslip PDF downloaded');
    } catch (error) {
      console.error('Error generating payslip PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Download payroll report as CSV
  const downloadReport = () => {
    if (calculatedPayslips.length === 0) {
      toast.error('No payroll data to download');
      return;
    }

    const headers = [
      'Employee ID', 'Employee Name', 'Department', 'Work State',
      'Gross Pay', 'Federal Tax', 'Social Security', 'Medicare',
      'State Tax', 'Pre-Tax Deductions', 'Post-Tax Deductions',
      'Net Pay', 'Pay Method'
    ];

    const rows = calculatedPayslips.map(p => [
      p.employeeId?.slice(0, 8) || '',
      p.employeeName || '',
      p.department || '',
      p.workState || '',
      p.grossPay?.toFixed(2) || '0.00',
      p.federalTax?.toFixed(2) || '0.00',
      p.socialSecurity?.toFixed(2) || '0.00',
      p.medicare?.toFixed(2) || '0.00',
      p.stateTax?.toFixed(2) || '0.00',
      p.totalPreTax?.toFixed(2) || '0.00',
      p.totalPostTax?.toFixed(2) || '0.00',
      p.netPay?.toFixed(2) || '0.00',
      p.bankAccounts?.length ? 'Direct Deposit' : 'Manual Check'
    ]);

    // Add summary row
    rows.push([]);
    rows.push(['SUMMARY']);
    rows.push(['Total Employees', summary.totalEmployees]);
    rows.push(['Total Gross Pay', summary.totalGross?.toFixed(2)]);
    rows.push(['Total Taxes', summary.totalTaxes?.toFixed(2)]);
    rows.push(['Total Deductions', summary.totalDeductions?.toFixed(2)]);
    rows.push(['Total Net Pay', summary.totalNet?.toFixed(2)]);
    rows.push(['Total Employer Cost', summary.totalEmployerCost?.toFixed(2)]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        const val = String(cell ?? '');
        return val.includes(',') || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `payroll-report-${runConfig.periodStart}-to-${runConfig.periodEnd}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success('Payroll report downloaded');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Run US Payroll</h1>
          <p className="text-gray-500 mt-1">Process payroll with complete tax calculations</p>
        </div>
        <Badge variant="outline" className="w-fit">
          Step {step} of 5: {STEPS[step - 1]?.name}
        </Badge>
      </div>

      {/* Progress Steps */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            {STEPS.map((s, index) => (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    step > s.id ? 'bg-emerald-500 text-white' :
                    step === s.id ? 'bg-teal-500 text-white' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s.id ? <CheckCircle2 className="w-5 h-5" /> : s.id}
                  </div>
                  <p className="text-xs mt-2 font-medium">{s.name}</p>
                  <p className="text-xs text-gray-400 hidden sm:block">{s.description}</p>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    step > s.id ? 'bg-emerald-500' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Setup */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-teal-600" />
              Payroll Run Setup
            </CardTitle>
            <CardDescription>Configure the pay period and run type</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Pay Group</Label>
                <Select
                  value={runConfig.payGroupId}
                  onValueChange={(v) => {
                    setRunConfig({...runConfig, payGroupId: v});
                    setSelectedEmployees([]); // Clear selection when pay group changes
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select pay group" />
                  </SelectTrigger>
                  <SelectContent>
                    {payGroups.map(group => (
                      <SelectItem key={group.id} value={group.id}>
                        {group.name} ({group.frequency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Run Type</Label>
                <Select
                  value={runConfig.runType}
                  onValueChange={(v) => setRunConfig({...runConfig, runType: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RUN_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <span className="font-medium">{type.label}</span>
                          <span className="text-gray-400 ml-2">- {type.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Period Start</Label>
                <Input
                  type="date"
                  value={runConfig.periodStart}
                  onChange={(e) => setRunConfig({...runConfig, periodStart: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Period End</Label>
                <Input
                  type="date"
                  value={runConfig.periodEnd}
                  onChange={(e) => setRunConfig({...runConfig, periodEnd: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label>Check Date (Pay Date)</Label>
                <Input
                  type="date"
                  value={runConfig.checkDate}
                  onChange={(e) => setRunConfig({...runConfig, checkDate: e.target.value})}
                />
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 text-blue-600" />
                    <div>
                      <p className="text-sm text-blue-600">Employees {runConfig.payGroupId && `(in Pay Group)`}</p>
                      <p className="text-2xl font-bold text-blue-900">{getFilteredEmployees().length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="text-sm text-green-600">Pay Period</p>
                      <p className="text-lg font-bold text-green-900">
                        {runConfig.periodStart && formatDate(new Date(runConfig.periodStart))} - {runConfig.periodEnd && formatDate(new Date(runConfig.periodEnd))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-8 h-8 text-purple-600" />
                    <div>
                      <p className="text-sm text-purple-600">Check Date</p>
                      <p className="text-lg font-bold text-purple-900">
                        {runConfig.checkDate && formatDate(new Date(runConfig.checkDate))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={runPreCheck} disabled={processing}>
                {processing ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Running Pre-Check...</>
                ) : (
                  <>Continue to Pre-Check <ChevronRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Pre-Check */}
      {step === 2 && (
        <div className="space-y-4">
          {validationErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Validation Errors</AlertTitle>
              <AlertDescription>
                {validationErrors.length} employee(s) have errors that must be resolved before processing.
              </AlertDescription>
            </Alert>
          )}

          {validationWarnings.length > 0 && (
            <Alert className="bg-amber-50 border-amber-200">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Warnings</AlertTitle>
              <AlertDescription className="text-amber-700">
                {validationWarnings.length} employee(s) have warnings. Review before proceeding.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Select Employees</CardTitle>
              <CardDescription>Choose employees to include in this payroll run</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-4">
                <Checkbox
                  checked={selectedEmployees.length > 0 && selectedEmployees.length === getFilteredEmployees().filter(e => !validationErrors.some(err => err.employeeId === e.id) && e.compensation).length}
                  onCheckedChange={toggleAllEmployees}
                />
                <Label>Select All Eligible</Label>
                <Badge className="ml-auto">
                  {selectedEmployees.length} of {getFilteredEmployees().length} selected
                </Badge>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Pay Type</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getFilteredEmployees().length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No employees found in the selected pay group
                      </TableCell>
                    </TableRow>
                  ) : getFilteredEmployees().map(emp => {
                    const hasError = validationErrors.some(e => e.employeeId === emp.id);
                    const hasWarning = validationWarnings.some(w => w.employeeId === emp.id);
                    const errorDetails = validationErrors.find(e => e.employeeId === emp.id);
                    const warningDetails = validationWarnings.find(w => w.employeeId === emp.id);

                    return (
                      <TableRow key={emp.id} className={hasError ? 'bg-red-50' : hasWarning ? 'bg-amber-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={selectedEmployees.includes(emp.id)}
                            onCheckedChange={() => toggleEmployeeSelection(emp.id)}
                            disabled={hasError || !emp.compensation}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-400 to-emerald-500 flex items-center justify-center text-white text-sm">
                              {emp.profile?.firstName?.[0]}{emp.profile?.lastName?.[0]}
                            </div>
                            <div>
                              <p className="font-medium">{emp.profile?.firstName} {emp.profile?.lastName}</p>
                              <p className="text-xs text-gray-500">{emp.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{emp.profile?.department || '-'}</TableCell>
                        <TableCell>
                          {emp.compensation ? (
                            <span className="capitalize">{emp.compensation.payType}</span>
                          ) : (
                            <span className="text-gray-400">Not configured</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {hasError ? (
                            <div>
                              <Badge variant="destructive">Error</Badge>
                              <ul className="text-xs text-red-600 mt-1">
                                {errorDetails?.errors.map((err, i) => (
                                  <li key={i}>• {err}</li>
                                ))}
                              </ul>
                            </div>
                          ) : hasWarning ? (
                            <div>
                              <Badge className="bg-amber-100 text-amber-700">Warning</Badge>
                              <ul className="text-xs text-amber-600 mt-1">
                                {warningDetails?.warnings.map((warn, i) => (
                                  <li key={i}>• {warn}</li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Ready
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <div className="flex justify-between pt-6">
                <Button variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button 
                  onClick={calculatePayroll} 
                  disabled={processing || selectedEmployees.length === 0}
                >
                  {processing ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Calculating...</>
                  ) : (
                    <><Calculator className="w-4 h-4 mr-2" /> Calculate Payroll</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 3 & 4: Calculate & Review */}
      {(step === 3 || step === 4) && (
        <div className="space-y-4">
          {/* AI Quick Analysis */}
          {step === 4 && calculatedPayslips.length > 0 && (() => {
            const avgPay = summary.totalGross / Math.max(summary.totalEmployees, 1);
            const anomalies = calculatedPayslips.filter(p => {
              const gross = p.earnings?.grossPay || 0;
              return gross > avgPay * 1.8 || gross < avgPay * 0.3;
            });
            const lowTaxCases = calculatedPayslips.filter(p => {
              const gross = p.earnings?.grossPay || 0;
              const fedTax = p.taxes?.federal?.amount || 0;
              return gross > 2000 && fedTax < gross * 0.05;
            });
            
            if (anomalies.length > 0 || lowTaxCases.length > 0) {
              return (
                <Alert className="border-violet-200 bg-gradient-to-r from-violet-50 to-purple-50">
                  <Brain className="h-4 w-4 text-violet-600" />
                  <AlertTitle className="text-violet-800 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    AI Analysis Detected Potential Issues
                  </AlertTitle>
                  <AlertDescription className="text-violet-700">
                    <ul className="mt-2 space-y-1">
                      {anomalies.length > 0 && (
                        <li className="flex items-center gap-2">
                          <AlertTriangle className="w-3 h-3 text-amber-500" />
                          {anomalies.length} employee(s) have pay significantly different from average ({anomalies.map(a => a.employeeName).join(', ')})
                        </li>
                      )}
                      {lowTaxCases.length > 0 && (
                        <li className="flex items-center gap-2">
                          <AlertCircle className="w-3 h-3 text-red-500" />
                          {lowTaxCases.length} employee(s) may have low federal tax withholding - verify W-4 settings
                        </li>
                      )}
                    </ul>
                  </AlertDescription>
                </Alert>
              );
            }
            return (
              <Alert className="border-emerald-200 bg-emerald-50">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertTitle className="text-emerald-800 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-500" />
                  AI Analysis: All Clear
                </AlertTitle>
                <AlertDescription className="text-emerald-700">
                  No anomalies detected. Payroll calculations appear within normal ranges.
                </AlertDescription>
              </Alert>
            );
          })()}

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-gray-500">Employees</p>
                <p className="text-2xl font-bold">{summary.totalEmployees}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-gray-500">Gross Pay</p>
                <p className="text-2xl font-bold text-gray-900">{formatUSDCurrency(summary.totalGross)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-gray-500">Total Taxes</p>
                <p className="text-2xl font-bold text-red-600">{formatUSDCurrency(summary.totalTaxes)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-gray-500">Deductions</p>
                <p className="text-2xl font-bold text-amber-600">{formatUSDCurrency(summary.totalDeductions)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-gray-500">Net Pay</p>
                <p className="text-2xl font-bold text-emerald-600">{formatUSDCurrency(summary.totalNet)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs text-gray-500">Employer Cost</p>
                <p className="text-2xl font-bold text-purple-600">{formatUSDCurrency(summary.totalEmployerCost)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Payroll Register */}
          <Card>
            <CardHeader>
              <CardTitle>Payroll Register</CardTitle>
              <CardDescription>Review individual employee calculations</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Fed Tax</TableHead>
                    <TableHead className="text-right">State Tax</TableHead>
                    <TableHead className="text-right">SS/Med</TableHead>
                    <TableHead className="text-right">Deductions</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {calculatedPayslips.map(calc => (
                    <TableRow key={calc.employeeId}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{calc.employeeName}</p>
                          <p className="text-xs text-gray-500">{calc.employee?.profile?.department}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatUSDCurrency(calc.earnings?.grossPay || 0)}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {formatUSDCurrency(calc.taxes?.federal?.amount || 0)}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {formatUSDCurrency(calc.taxes?.state?.amount || 0)}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {formatUSDCurrency((calc.taxes?.socialSecurity?.amount || 0) + (calc.taxes?.medicare?.amount || 0))}
                      </TableCell>
                      <TableCell className="text-right text-amber-600">
                        {formatUSDCurrency((calc.preTaxDeductions?.total || 0) + (calc.postTaxDeductions?.total || 0))}
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-600">
                        {formatUSDCurrency(calc.netPay)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedPayslip(calc);
                            setShowPayslipModal(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex justify-between pt-6">
                <Button variant="outline" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button 
                  onClick={handleSubmitClick}
                  disabled={processing}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                >
                  {processing ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Submit Payroll</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 5: Complete */}
      {step === 5 && (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payroll Submitted Successfully!</h2>
            <p className="text-gray-500 mb-6">
              Payroll for {summary.totalEmployees} employees has been processed.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Total Gross</p>
                <p className="text-xl font-bold">{formatUSDCurrency(summary.totalGross)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Total Taxes</p>
                <p className="text-xl font-bold text-red-600">{formatUSDCurrency(summary.totalTaxes)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Total Net</p>
                <p className="text-xl font-bold text-emerald-600">{formatUSDCurrency(summary.totalNet)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Employer Cost</p>
                <p className="text-xl font-bold text-purple-600">{formatUSDCurrency(summary.totalEmployerCost)}</p>
              </div>
            </div>

            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={downloadReport}>
                <Download className="w-4 h-4 mr-2" /> Download Report
              </Button>
              <Button onClick={() => {
                setStep(1);
                setCalculatedPayslips([]);
                setSummary({
                  totalEmployees: 0,
                  totalGross: 0,
                  totalTaxes: 0,
                  totalDeductions: 0,
                  totalNet: 0,
                  totalEmployerCost: 0
                });
              }}>
                <Play className="w-4 h-4 mr-2" /> Run Another Payroll
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payslip Detail Modal */}
      <Dialog open={showPayslipModal} onOpenChange={setShowPayslipModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payslip Details</DialogTitle>
            <DialogDescription>
              {selectedPayslip?.employeeName} - Pay Period: {runConfig.periodStart} to {runConfig.periodEnd}
            </DialogDescription>
          </DialogHeader>

          {selectedPayslip && (
            <div className="space-y-6">
              {/* Earnings */}
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" /> Earnings
                </h4>
                <Table>
                  <TableBody>
                    {selectedPayslip.earnings?.breakdown?.map((earn, i) => (
                      <TableRow key={i}>
                        <TableCell>{earn.name}</TableCell>
                        <TableCell className="text-right">
                          {earn.hours && `${earn.hours} hrs @ ${formatUSDCurrency(earn.rate)}`}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatUSDCurrency(earn.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-50">
                      <TableCell colSpan={2} className="font-bold">Gross Pay</TableCell>
                      <TableCell className="text-right font-bold">
                        {formatUSDCurrency(selectedPayslip.earnings?.grossPay || 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Taxes */}
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Building className="w-4 h-4 text-red-600" /> Taxes
                </h4>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>Federal Income Tax</TableCell>
                      <TableCell className="text-right text-red-600">
                        -{formatUSDCurrency(selectedPayslip.taxes?.federal?.amount || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Social Security (6.2%)</TableCell>
                      <TableCell className="text-right text-red-600">
                        -{formatUSDCurrency(selectedPayslip.taxes?.socialSecurity?.amount || 0)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Medicare (1.45%)</TableCell>
                      <TableCell className="text-right text-red-600">
                        -{formatUSDCurrency(selectedPayslip.taxes?.medicare?.amount || 0)}
                      </TableCell>
                    </TableRow>
                    {selectedPayslip.taxes?.state?.amount > 0 && (
                      <TableRow>
                        <TableCell>{selectedPayslip.taxes.state.stateName} State Tax</TableCell>
                        <TableCell className="text-right text-red-600">
                          -{formatUSDCurrency(selectedPayslip.taxes.state.amount)}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow className="bg-red-50">
                      <TableCell className="font-bold">Total Taxes</TableCell>
                      <TableCell className="text-right font-bold text-red-600">
                        -{formatUSDCurrency(selectedPayslip.taxes?.totalTaxes || 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Deductions */}
              {(selectedPayslip.preTaxDeductions?.deductions?.length > 0 || 
                selectedPayslip.postTaxDeductions?.deductions?.length > 0) && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-amber-600" /> Deductions
                  </h4>
                  <Table>
                    <TableBody>
                      {selectedPayslip.preTaxDeductions?.deductions?.map((ded, i) => (
                        <TableRow key={`pre-${i}`}>
                          <TableCell>{ded.name} (Pre-tax)</TableCell>
                          <TableCell className="text-right text-amber-600">
                            -{formatUSDCurrency(ded.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {selectedPayslip.postTaxDeductions?.deductions?.map((ded, i) => (
                        <TableRow key={`post-${i}`}>
                          <TableCell>{ded.name} (Post-tax)</TableCell>
                          <TableCell className="text-right text-amber-600">
                            -{formatUSDCurrency(ded.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Net Pay */}
              <div className="bg-emerald-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-emerald-900">Net Pay</span>
                  <span className="text-2xl font-bold text-emerald-600">
                    {formatUSDCurrency(selectedPayslip.netPay)}
                  </span>
                </div>
              </div>

              {/* Employer Costs */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-2 text-gray-500">Employer Costs (Not deducted from pay)</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span>Employer SS:</span>
                    <span>{formatUSDCurrency(selectedPayslip.employerTaxes?.socialSecurity || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Employer Medicare:</span>
                    <span>{formatUSDCurrency(selectedPayslip.employerTaxes?.medicare || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>FUTA:</span>
                    <span>{formatUSDCurrency(selectedPayslip.employerTaxes?.futa || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>SUI:</span>
                    <span>{formatUSDCurrency(selectedPayslip.employerTaxes?.sui || 0)}</span>
                  </div>
                </div>
                <div className="flex justify-between mt-2 pt-2 border-t font-medium">
                  <span>Total Employer Cost:</span>
                  <span>{formatUSDCurrency(selectedPayslip.totalEmployerCost || 0)}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayslipModal(false)}>
              Close
            </Button>
            <Button onClick={() => downloadIndividualPayslip(selectedPayslip)}>
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Payroll Confirmation Dialog */}
      <Dialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Payroll Submission
            </DialogTitle>
            <DialogDescription>
              You are about to submit payroll for processing.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">This action cannot be undone</AlertTitle>
              <AlertDescription className="text-amber-700">
                Once submitted, payroll records will be finalized and payslips will be generated for all employees.
              </AlertDescription>
            </Alert>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Employees:</span>
                <span className="font-medium">{calculatedPayslips.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Gross:</span>
                <span className="font-medium">{formatUSDCurrency(summary.totalGross)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Net:</span>
                <span className="font-medium text-emerald-600">{formatUSDCurrency(summary.totalNet)}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Pay Period:</span>
                <span className="font-medium">{runConfig.periodStart} to {runConfig.periodEnd}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Check Date:</span>
                <span className="font-medium">{runConfig.checkDate}</span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowSubmitConfirm(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitPayroll}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Yes, Submit Payroll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
